import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API, authHeaders, useAdminAuthError } from "@/lib/adminApi";
import PageHeader from "@/components/admin/PageHeader";
import StatusBadge from "@/components/admin/StatusBadge";
import RecoveryRing from "@/components/admin/RecoveryRing";
import Spinner from "@/components/admin/Spinner";
import NewMemberPanel from "@/components/admin/NewMemberPanel";
import MemberPanel from "@/components/admin/MemberPanel";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

// Mêmes libellés/couleurs que la colonne "Statut {year}" du tableau ci-dessous,
// pour que la sélection d'export corresponde à ce que l'admin voit à l'écran.
const EXPORT_STATUS_OPTIONS = [
  { key: "paid", label: "À jour", dot: "bg-emerald-500" },
  { key: "partial", label: "Partielle", dot: "bg-amber-500" },
  { key: "pending", label: "En attente", dot: "bg-slate-400" },
  { key: "deceased", label: "Décédé", dot: "bg-slate-600" },
];

export const MembersPage = () => {
  const { year } = useOutletContext();
  const handleAuthError = useAdminAuthError();
  const [searchParams] = useSearchParams();

  const [members, setMembers] = useState([]);
  const [adminPayments, setAdminPayments] = useState([]);
  const [recovery, setRecovery] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportPopoverOpen, setExportPopoverOpen] = useState(false);
  const [exportStatuses, setExportStatuses] = useState(() =>
    Object.fromEntries(EXPORT_STATUS_OPTIONS.map((o) => [o.key, true]))
  );
  // Ouvre directement la fiche d'un membre via /app/membres?member=<id> (ex. depuis
  // la page Sinistres).
  const [selectedMemberId, setSelectedMemberId] = useState(() => searchParams.get("member"));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Pas de filtre `year` ici : `year` sur le document membre est son année
      // d'inscription (figée), pas l'exercice de cotisation. La liste montre
      // tous les membres de l'association ; seul le statut ci-dessous varie
      // avec l'exercice sélectionné, via les paiements de l'année.
      const [membersRes, paymentsRes, recoveryRes] = await Promise.all([
        axios.get(`${API}/members/`, { headers: authHeaders() }),
        axios.get(`${API}/payments/admin-list?year=${year}`, { headers: authHeaders() }),
        axios.get(`${API}/payments/recovery?year=${year}`, { headers: authHeaders() }),
      ]);
      setMembers(membersRes.data);
      setAdminPayments(paymentsRes.data);
      setRecovery(recoveryRes.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erreur lors du chargement des membres.");
    } finally {
      setLoading(false);
    }
  }, [year, handleAuthError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleExportPdf = async () => {
    const selected = Object.entries(exportStatuses).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) {
      toast.error("Sélectionnez au moins un statut à exporter.");
      return;
    }
    setExportingPdf(true);
    try {
      const params = new URLSearchParams({ year, statuses: selected.join(",") });
      const res = await axios.get(`${API}/members/export/pdf?${params.toString()}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `membres_${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setExportPopoverOpen(false);
    } catch {
      toast.error("Erreur lors de la génération du PDF.");
    } finally {
      setExportingPdf(false);
    }
  };

  // Map member_id → dernier paiement complété de l'exercice sélectionné, pour
  // l'affichage des colonnes Mode/Date/Montant (pas pour le statut "à jour" —
  // cf. `recovery`, sensible au montant, ci-dessous).
  const paymentByMember = adminPayments.reduce((acc, p) => {
    if (p.member_id && p.status === "completed") {
      if (!acc[p.member_id]) acc[p.member_id] = p;
    }
    return acc;
  }, {});

  // Source de vérité du statut "à jour" — solde nul (`recovery`, alimenté par
  // GET /payments/recovery), pas l'existence d'un paiement ni le scalaire
  // member.payment_status. Un versement partiel n'est plus compté "à jour".
  const filteredMembers = members.filter((m) => {
    const fullName = `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase();
    const matchSearch = fullName.includes(search.toLowerCase());
    const isDeceased = m.status === "deceased";
    const memberRecovery = recovery[m.id];
    const isSettled = !!memberRecovery?.is_settled;
    const isPartial = !isSettled && (memberRecovery?.paid_amount ?? 0) > 0;
    const matchStatus =
      statusFilter === "all" ||
      (statusFilter === "deceased" && isDeceased) ||
      (statusFilter === "paid" && !isDeceased && isSettled) ||
      (statusFilter === "partial" && !isDeceased && isPartial) ||
      (statusFilter === "pending" && !isDeceased && !isSettled && !isPartial);
    return matchSearch && matchStatus;
  });

  return (
    <section className="fade-up fade-up-1">
      <PageHeader
        title="Membres"
        subtitle={`${filteredMembers.length} résultat${filteredMembers.length !== 1 ? "s" : ""} · statut exercice ${year}`}
        actions={
          <>
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 w-44 transition-all"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value="all">Tous</option>
              <option value="paid">À jour</option>
              <option value="partial">Partiel</option>
              <option value="pending">En attente</option>
              <option value="deceased">Décédé</option>
            </select>
            <Popover open={exportPopoverOpen} onOpenChange={setExportPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
                >
                  <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  <span>Exporter PDF</span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Statuts à inclure
                </p>
                <div className="flex flex-col gap-2.5 mb-4">
                  {EXPORT_STATUS_OPTIONS.map((opt) => (
                    <label key={opt.key} className="flex items-center gap-2.5 cursor-pointer select-none">
                      <Checkbox
                        checked={exportStatuses[opt.key]}
                        onCheckedChange={(checked) =>
                          setExportStatuses((prev) => ({ ...prev, [opt.key]: !!checked }))
                        }
                      />
                      <span className={`w-1.5 h-1.5 rounded-full ${opt.dot}`} />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
                <button
                  onClick={handleExportPdf}
                  disabled={exportingPdf}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.97] disabled:opacity-50"
                  style={{ backgroundColor: "#059669" }}
                >
                  {exportingPdf && (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  )}
                  <span>{exportingPdf ? "Export…" : "Exporter"}</span>
                </button>
              </PopoverContent>
            </Popover>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90 active:scale-[0.97] text-white"
              style={{ backgroundColor: "#059669" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              Nouveau membre
            </button>
          </>
        }
      />

      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32"><Spinner /></div>
        ) : filteredMembers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm gap-2">
            <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p>Aucun membre trouvé.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <p className="text-[10px] text-slate-400 text-right px-4 pt-2 pb-0 sm:hidden">← Faites défiler →</p>
            <table className="text-sm" style={{ minWidth: "720px", width: "100%" }}>
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  {[
                    "Nom", "Prénom", "Téléphone", "Code postal",
                    `Statut ${year}`, "Mode", "Date paiement", "Montant",
                  ].map((label) => (
                    <th
                      key={label}
                      className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((m, i) => {
                  const p = paymentByMember[m.id];
                  const isDeceased = m.status === "deceased";
                  const memberRecovery = recovery[m.id];
                  const recoveryRate = memberRecovery?.recovery_rate ?? 0;
                  const isSettled = !!memberRecovery?.is_settled;
                  const isPartial = !isSettled && (memberRecovery?.paid_amount ?? 0) > 0;
                  const payDate = p?.created_at
                    ? new Date(p.created_at).toLocaleDateString("fr-FR")
                    : "—";
                  const payMethod = p?.payment_method || p?.provider || "—";
                  const payAmount = memberRecovery?.paid_amount
                    ? `${Number(memberRecovery.paid_amount).toLocaleString("fr-FR")} €`
                    : "—";
                  return (
                    <tr
                      key={m.id}
                      onClick={() => setSelectedMemberId(m.id)}
                      className={`hover:bg-slate-50/40 transition-colors cursor-pointer ${i < filteredMembers.length - 1 ? "border-b border-slate-50" : ""}`}
                    >
                      <td className="px-4 py-3.5 font-semibold text-slate-900 whitespace-nowrap">{m.last_name || "—"}</td>
                      <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap">{m.first_name || "—"}</td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {m.phone
                          ? <a href={`tel:${m.phone}`} onClick={(e) => e.stopPropagation()} className="font-mono text-xs text-slate-700 hover:text-emerald-600 transition-colors select-all">{m.phone}</a>
                          : <span className="text-slate-400">—</span>
                        }
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 font-mono text-xs whitespace-nowrap">{m.address_postal_code || "—"}</td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {isDeceased ? (
                          <StatusBadge color="slate">Décédé</StatusBadge>
                        ) : (
                          <div className="flex items-center gap-2">
                            <RecoveryRing rate={recoveryRate} />
                            <span className={`text-xs font-medium ${
                              isSettled ? "text-emerald-700" : isPartial ? "text-amber-700" : "text-slate-400"
                            }`}>
                              {isSettled ? "À jour" : isPartial ? "Partielle" : "En attente"}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap capitalize">{payMethod}</td>
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">{payDate}</td>
                      <td className="px-4 py-3.5 text-slate-700 text-xs font-medium whitespace-nowrap">{payAmount}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <NewMemberPanel
          year={year}
          onClose={() => setShowForm(false)}
          onSuccess={() => { setShowForm(false); fetchAll(); }}
        />
      )}

      {selectedMemberId && (
        <MemberPanel
          memberId={selectedMemberId}
          onClose={() => setSelectedMemberId(null)}
          onChanged={fetchAll}
        />
      )}
    </section>
  );
};

export default MembersPage;
